import type { ReactNode } from 'react'
import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { cacheHeaders } from '~/lib/site/cache'
import {
  DEFAULT_COVER,
  eventTimeZone,
  facebookEventUrl,
  formatEventDate,
  formatEventTime,
  formatEventZone,
  fullLocation,
  googleCalendarUrl,
  icsDataUri,
  type AdbEvent,
} from '~/lib/adb/client'
import { chapterEventsQuery, useChapterEvents } from '~/lib/adb/chapter'
import { parsePageId } from '~/lib/adb/page-id'
import {
  SITE_URL,
  absoluteUrl,
  isoWithOffset,
  jsonLd,
  organizationRef,
  pageMeta,
} from '~/lib/site/seo'
import { Button } from '~/components/ui/Button'
import { Container } from '~/components/ui/Container'
import { Cover, RsvpLinks } from '~/components/events/EventCard'
import { PageTitle } from '~/components/ui/PageTitle'
import { pageTop } from '~/components/ui/PageHero'

export const Route = createFileRoute('/events/$pageId/$eventId')({
  // Parse the PageID once, so the loader and component get a number.
  params: {
    parse: ({ pageId, eventId }) => ({ pageId: parsePageId(pageId), eventId }),
    stringify: ({ pageId, eventId }) => ({
      pageId: String(pageId),
      eventId,
    }),
  },
  // A flaky API yields `failed` and a missing event yields null; only a
  // malformed PageID is a 404.
  // Events created after the last build land here via the SPA shell, so the
  // loader (running in the browser) finds them in that chapter's live list.
  loader: async ({ params, context: { queryClient } }) => {
    const { pageId } = params
    if (Number.isNaN(pageId)) throw notFound()
    try {
      // Through the query cache, which the page's `useChapterEvents` shares.
      const events = await queryClient.ensureQueryData(
        chapterEventsQuery(pageId),
      )
      return {
        pageId,
        event: events.find((e) => e.ID === params.eventId) ?? null,
        failed: false,
      }
    } catch (e) {
      console.warn(`event ${params.eventId} unavailable`, e)
      return { pageId, event: null, failed: true }
    }
  },
  headers: () => cacheHeaders.events,
  head: ({ loaderData, params }) => {
    const ev = loaderData?.event
    if (!ev) return pageMeta({ title: 'Event' })
    const title = `${ev.Name} | Direct Action Everywhere`
    const tz = eventTimeZone(ev)
    const description =
      ev.Description.replace(/\s+/g, ' ').trim().slice(0, 200) ||
      `${ev.Name}: ${formatEventDate(ev.StartTime, tz)}, ${formatEventTime(ev.StartTime, tz)}${ev.LocationName ? ` at ${ev.LocationName}` : ''}. Organized by Direct Action Everywhere.`
    const url = absoluteUrl(`/events/${params.pageId}/${ev.ID}`)
    const hasPlace = Boolean(ev.LocationAddress || ev.LocationCity || ev.Lat)
    const place = {
      '@type': 'Place',
      name: ev.LocationName || ev.LocationCity || 'Event location',
      address: {
        '@type': 'PostalAddress',
        ...(ev.LocationAddress ? { streetAddress: ev.LocationAddress } : {}),
        ...(ev.LocationCity ? { addressLocality: ev.LocationCity } : {}),
        ...(ev.LocationState ? { addressRegion: ev.LocationState } : {}),
        ...(ev.LocationZip ? { postalCode: ev.LocationZip } : {}),
        ...(ev.LocationCountry ? { addressCountry: ev.LocationCountry } : {}),
      },
      ...(ev.Lat || ev.Lng
        ? {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: ev.Lat,
              longitude: ev.Lng,
            },
          }
        : {}),
    }
    const endsAfterStart = Date.parse(ev.EndTime) > Date.parse(ev.StartTime)
    return {
      scripts: [
        jsonLd({
          '@type': 'Event',
          name: ev.Name,
          url,
          description,
          startDate: isoWithOffset(ev.StartTime, tz),
          ...(endsAfterStart ? { endDate: isoWithOffset(ev.EndTime, tz) } : {}),
          eventStatus: ev.IsCanceled
            ? 'https://schema.org/EventCancelled'
            : 'https://schema.org/EventScheduled',
          eventAttendanceMode: hasPlace
            ? 'https://schema.org/OfflineEventAttendanceMode'
            : 'https://schema.org/OnlineEventAttendanceMode',
          location: hasPlace
            ? place
            : { '@type': 'VirtualLocation', url: facebookEventUrl(ev) },
          image: [ev.Cover || `${SITE_URL}${DEFAULT_COVER}`],
          organizer: organizationRef,
        }),
      ],
      // Facebook CDN URLs expire; fine for a build's lifetime of link previews.
      ...pageMeta({ title, description, image: ev.Cover || undefined }),
    }
  },
  component: EventPage,
})

const URL_RE = /(https?:\/\/[^\s<]+)/g

/** Preserves line breaks (via CSS) and turns URLs into links. */
function Linkified({ text }: { text: string }) {
  return (
    <>
      {text.split(URL_RE).map((part, i) => {
        if (i % 2 === 0) return part
        const url = part.replace(/[.,;:!?)\]]+$/, '')
        const rest = part.slice(url.length)
        return (
          <span key={i}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="font-bold break-all text-accent hover:underline"
            >
              {url}
            </a>
            {rest}
          </span>
        )
      })}
    </>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-5">
      <p className="mb-1 text-sm leading-5 font-semibold text-fg-subtle uppercase">
        {label}
      </p>
      <div className="text-base leading-6 md:text-lg md:leading-[26px]">
        {children}
      </div>
    </div>
  )
}

function EventPage() {
  const seed = Route.useLoaderData()
  const params = Route.useParams()
  const { events, loading } = useChapterEvents(seed.pageId)
  // Prefer fresh data; a failed or windowed-out refresh keeps the loaded copy.
  const event: AdbEvent | null =
    events?.find((e) => e.ID === params.eventId) ?? seed.event

  if (!event) {
    return (
      <Shell>
        <PageTitle size="md" className="mb-6">
          {loading
            ? 'Loading event…'
            : seed.failed
              ? "Couldn't load event"
              : 'Event not found'}
        </PageTitle>
        {!loading && (
          <p className="mb-6">
            {seed.failed
              ? 'We had trouble reaching the events service. Please try again in a moment.'
              : 'This event may have been canceled, removed or made private.'}
          </p>
        )}
        <Link to="/events" className="font-bold text-accent hover:underline">
          Browse upcoming events
        </Link>
      </Shell>
    )
  }

  const tz = eventTimeZone(event)
  const sameDay =
    formatEventDate(event.StartTime, tz) === formatEventDate(event.EndTime, tz)
  const hasEnd = Date.parse(event.EndTime) > Date.parse(event.StartTime)
  const address = [
    event.LocationAddress,
    [event.LocationCity, event.LocationState].filter(Boolean).join(', '),
    event.LocationZip,
  ]
    .filter(Boolean)
    .join(' ')
  const mapsQuery =
    event.Lat || event.Lng ? `${event.Lat},${event.Lng}` : fullLocation(event)

  return (
    <Shell>
      <Cover
        src={event.Cover}
        className="mb-8 max-h-[500px] w-full rounded-lg object-cover"
      />
      <PageTitle size="md" className="mb-6">
        {event.Name}
      </PageTitle>
      {event.IsCanceled && (
        <p className="mb-6 rounded bg-danger-surface p-3 font-bold text-danger-surface-fg">
          This event has been canceled.
        </p>
      )}
      <div className="grid gap-x-10 md:grid-cols-[1fr_1.4fr]">
        <div>
          <Row label="When">
            {formatEventDate(event.StartTime, tz)}
            <br />
            {formatEventTime(event.StartTime, tz)}
            {hasEnd &&
              ` – ${sameDay ? '' : `${formatEventDate(event.EndTime, tz)}, `}${formatEventTime(event.EndTime, tz)}`}{' '}
            {formatEventZone(event.StartTime, tz)}
          </Row>
          {(event.LocationName || address) && (
            <Row label="Where">
              {event.LocationName && <strong>{event.LocationName}</strong>}
              {address && (
                <>
                  <br />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-accent hover:underline"
                  >
                    {address}
                  </a>
                </>
              )}
            </Row>
          )}
          <Row label="Who's going">
            {event.AttendingCount} attending | {event.InterestedCount}{' '}
            interested
          </Row>
          <div className="mb-6 flex flex-col gap-3">
            <Button
              href={facebookEventUrl(event)}
              target="_blank"
              rel="noreferrer"
            >
              RSVP on Facebook
            </Button>
            {event.EventbriteURL && (
              <Button
                href={event.EventbriteURL}
                target="_blank"
                rel="noreferrer"
                variant="outline"
              >
                RSVP on Eventbrite
              </Button>
            )}
          </div>
          <p className="mb-2 text-base">
            <strong>Add to calendar:</strong>{' '}
            <a
              href={googleCalendarUrl(event)}
              target="_blank"
              rel="noreferrer"
              className="font-bold text-accent hover:underline"
            >
              Google Calendar
            </a>
            {' | '}
            <a
              href={icsDataUri(event)}
              download={`dxe-event-${event.ID}.ics`}
              className="font-bold text-accent hover:underline"
            >
              Apple / Outlook (.ics)
            </a>
          </p>
          <p className="mb-8 text-sm text-fg-muted">
            Times shown in the event's local time zone (
            {tz === 'UTC' ? 'UTC' : tz.replaceAll('_', ' ')}).
          </p>
        </div>
        <div>
          <h2 className="mt-0 mb-3 text-2xl font-bold text-fg">About</h2>
          <div className="text-base leading-6 break-words whitespace-pre-line md:text-lg md:leading-[26px]">
            <Linkified text={event.Description} />
          </div>
          <p className="mt-8 mb-0 text-base">
            <RsvpLinks event={event} />
          </p>
        </div>
      </div>
    </Shell>
  )
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className={`${pageTop} pb-10 md:pb-[60px]`}>
      <Container width="wide">
        <p className="mb-6">
          <Link to="/events" className="font-bold text-accent hover:underline">
            ← All events
          </Link>
        </p>
        {children}
      </Container>
    </div>
  )
}
